import { ColumnNameToIndexMap, getReportOptions, updateReportDate } from "./helpers/common";
import { cacheableData } from "../workbook/cache";
import { track } from "../../clients/mixpanel";
import { getGCGRReportData, getProjectReportData, setPrintAreaToUsedRange } from "../workbook";
import { ValidationError } from "../../errors";
import { createColumnNameToIndexMap } from "./helpers/print";
import { calculatedRangeNames, rowNames } from "./helpers/rangeNames";
import { RowFormat, printFormattedData } from "./helpers/print";
import {
  fetchSelectedGCGR,
  fetchGCGRsByProjectId,
  fetchGCGRExport,
  fetchGCGRDetailGroups,
  fetchEmployees,
  fetchPositions,
} from "../../api";
import { transformStaffDetails, transformGcgrLineItems } from "../datasources/gcgr";
import { StaffDetail, GCGRLineItem, GCGR } from "../../interfaces/GCGRs";

export interface GCGRReportOptions extends cacheableData {
  groupRows: boolean;
  subtotalsInHeader: boolean;
}

interface GCGRarrayArgs {
  lineItems?: Record<string, any>[];
  staffItems?: Record<string, any>[];
  detailGroups?: Record<string, any>[];
  staffPhases?: Record<string, any>[];
  columnMap: ColumnNameToIndexMap;
  options: GCGRReportOptions;
}

export const gcStaffDefaultOptions = {
  groupRows: true,
  subtotalsInHeader: true,
} as GCGRReportOptions;

export const gcgrLineItemsDefaultOptions = {
  groupRows: true,
  subtotalsInHeader: true,
} as GCGRReportOptions;

const resolveProjectId = async (projectIdParam?: string): Promise<string> => {
  if (projectIdParam) return projectIdParam;
  const reportData = await getProjectReportData();
  if (reportData.length === 0) {
    throw new ValidationError("No project loaded. Please select a project first.");
  }
  return reportData[0].project_id as string;
};

// Resolve the report's GCGR — whatever the metadata row names — and fetch its full export payload.
// Returns null when no GCGR is loaded, the project no longer has it, or the export came back empty;
// throws the API error when either fetch fails.
const fetchResolvedGCGRExport = async (projectId: string): Promise<{ gcgr: GCGR; fullGCGR: GCGR } | null> => {
  const gcgrId = (await getGCGRReportData())[0]?.gcgr_id;
  if (!gcgrId) return null;
  const gcgrResult = await fetchSelectedGCGR(projectId, gcgrId);
  if (!gcgrResult.success) throw gcgrResult.error;
  const gcgr = gcgrResult.data;
  if (!gcgr) return null;
  const fullGCGRRes = await fetchGCGRExport(projectId, gcgr.id);
  if (!fullGCGRRes.success) throw fullGCGRRes.error;
  return fullGCGRRes.data ? { gcgr, fullGCGR: fullGCGRRes.data } : null;
};

export const fetchGCGRsForReport = async (projectIdParam?: string): Promise<Record<string, any>[]> => {
  let projectId = projectIdParam;
  if (!projectId) {
    const reportData = await getProjectReportData();
    if (reportData.length === 0) return [];
    projectId = reportData[0].project_id as string;
  }
  const result = await fetchGCGRsByProjectId(projectId);
  // Propagate fetch failures — callers distinguish "failed" (fall back to workbook tables)
  // from "project has none"; swallowing to [] would disguise a transient API failure as no-data
  if (!result.success) throw result.error;
  // Active only, to mirror the __GCGR table this report is generated against.
  return result.data ?? [];
};

export const fetchGCGRStaffWithDetails = async (projectIdParam?: string): Promise<Record<string, any>[]> => {
  const projectId = await resolveProjectId(projectIdParam);
  const resolved = await fetchResolvedGCGRExport(projectId);
  if (!resolved) return [];
  const { gcgr, fullGCGR } = resolved;

  const staffData: StaffDetail[] = fullGCGR.gcgr_staff_details || [];
  const phasesData: Record<string, any>[] = fullGCGR.gcgr_phases || [];
  const [employeesRes, positionsRes] = await Promise.all([fetchEmployees(), fetchPositions()]);
  // Throw on failure (main behavior) — swallowing to [] would print blank employee names,
  // cost codes, and default multipliers with no error surfaced
  if (!employeesRes.success) throw employeesRes.error;
  if (!positionsRes.success) throw positionsRes.error;
  const employeesData = employeesRes.data || [];
  const positionsData = positionsRes.data || [];

  return transformStaffDetails(staffData, phasesData, employeesData, positionsData, gcgr);
};

export const fetchGCGRReportAvailability = async (
  projectId: string
): Promise<{ hasStaff: boolean; hasLineItems: boolean }> => {
  const resolved = await fetchResolvedGCGRExport(projectId);
  return {
    hasStaff: (resolved?.fullGCGR.gcgr_staff_details?.length ?? 0) > 0,
    hasLineItems: (resolved?.fullGCGR.gcgr_detail_line_items?.length ?? 0) > 0,
  };
};

export const fetchGCGRLineItemsWithGroups = async (projectIdParam?: string): Promise<Record<string, any>[]> => {
  const projectId = await resolveProjectId(projectIdParam);
  const resolved = await fetchResolvedGCGRExport(projectId);
  if (!resolved) return [];
  const { fullGCGR } = resolved;

  const lineItemsData: GCGRLineItem[] = fullGCGR.gcgr_detail_line_items || [];
  const groupsRes = await fetchGCGRDetailGroups();
  if (!groupsRes.success) throw groupsRes.error;
  const groupsData = groupsRes.data || [];

  return transformGcgrLineItems(lineItemsData, groupsData);
};

export const generateGcStaffReport = async (worksheet: Excel.Worksheet) => {
  const startTime = performance.now();
  const options = (await getReportOptions(worksheet, gcStaffDefaultOptions)) as GCGRReportOptions;

  const projectId = await resolveProjectId();
  const resolved = await fetchResolvedGCGRExport(projectId);
  if (!resolved) {
    throw new ValidationError("Load GCGR data before generating report.");
  }
  const { gcgr, fullGCGR } = resolved;

  const staffData: StaffDetail[] = fullGCGR.gcgr_staff_details || [];
  if (!staffData.length) {
    throw new ValidationError("Load GCGR data before generating report.");
  }

  const phasesData: Record<string, any>[] = fullGCGR.gcgr_phases || [];
  const [employeesRes, positionsRes] = await Promise.all([fetchEmployees(), fetchPositions()]);
  // Throw on failure (main behavior) — swallowing to [] would print blank employee names,
  // cost codes, and default multipliers with no error surfaced
  if (!employeesRes.success) throw employeesRes.error;
  if (!positionsRes.success) throw positionsRes.error;
  const employeesData = employeesRes.data || [];
  const positionsData = positionsRes.data || [];

  const staffDetails = transformStaffDetails(staffData, phasesData, employeesData, positionsData, gcgr);
  const staffPhases = phasesData;

  const columnMap = await createColumnNameToIndexMap(worksheet);

  // STAFF DETAIL SECTION
  const sortedStaff = [...staffDetails].sort((a, b) => {
    if (a.phase_category !== b.phase_category) {
      if (a.phase_category === "precon") return -1;
      if (b.phase_category === "precon") return 1;
      return a.phase_category.localeCompare(b.phase_category);
    }
    if (a.phase !== b.phase) return a.phase.localeCompare(b.phase);
    return (a.sort_order ?? 0) - (b.sort_order ?? 0);
  });

  const { dataArray: staffDataArray, formatList: staffFormatList } = gcStaffReportArray({
    staffItems: sortedStaff,
    staffPhases: staffPhases,
    columnMap,
    options,
  });

  const startColumnIndex = columnMap.startColumnIndex;
  await printFormattedData({
    dataArray: staffDataArray,
    formatList: staffFormatList,
    worksheet,
    insertAtRangeName: rowNames.gcgrStaffItems,
    startColumnIndex,
    options,
    columnMap,
  });

  await updateReportDate(worksheet);
  await setPrintAreaToUsedRange(worksheet);

  track("excel.createReport.gcStaff", {
    loadTime: (performance.now() - startTime) / 1000,
  });
};

const gcStaffReportArray = ({
  staffItems,
  staffPhases,
  columnMap,
  options,
}: GCGRarrayArgs): { dataArray: (string | number | null)[][]; formatList: RowFormat[] } => {
  // Sort data by `order` and `name`
  const formatList: RowFormat[] = [];
  const { subtotalsInHeader, addBlankRowBetweenGroups } = options;

  // Determine the maximum column index from columnMap
  let maxColumnIndex = Math.max(...Object.values(columnMap));
  const indentColumn = columnMap.position_name;
  let updatedColumnMap = { ...columnMap };

  // Recursive function to group data
  const groupData = (items: Record<string, any>[]): (string | number | null)[][] => {
    const groupByProp = "phase";
    const sortedItems = items.sort((a, b) => {
      // Sort by phase_category, with "precon" always first
      if (a.phase_category === "precon" && b.phase_category !== "precon") return -1;
      if (a.phase_category !== "precon" && b.phase_category === "precon") return 1;

      // If same phase_category, sort by phase alphabetically
      const phaseCompare = a.phase.localeCompare(b.phase);
      if (phaseCompare !== 0) return phaseCompare;

      // If same phase, sort by sort_order
      return (a.sort_order ?? 0) - (b.sort_order ?? 0);
    });
    const phaseCatGrouped = sortedItems.reduce(
      (acc, item) => {
        acc[item.phase_category] = acc[item.phase_category] || [];
        acc[item.phase_category].push(item);
        return acc;
      },
      {} as { [key: string]: Record<string, any>[] }
    );
    const phaseCatPhases = sortedItems.reduce(
      (acc, item) => {
        if (!acc[item.phase_category]) {
          acc[item.phase_category] = new Set<string>(); // Use Set for uniqueness
        }
        acc[item.phase_category].add(item.phase); // Add to Set
        return acc;
      },
      {} as { [key: string]: Set<string> }
    );
    const sortedPhaseCategories = Object.keys(phaseCatGrouped).sort((a, b) => {
      if (a === "precon" && b !== "precon") return -1;
      if (a !== "precon" && b === "precon") return 1;
      return a.localeCompare(b);
    });

    const result: (string | number | null)[][] = [];

    sortedPhaseCategories.forEach((phaseCat, index) => {
      // Add section header for phase category
      const sectionHeader: (string | number | null)[] = Array(maxColumnIndex + 1).fill(null);
      sectionHeader[0] =
        phaseCat === "precon" ? "Preconstruction" : phaseCat.charAt(0).toUpperCase() + phaseCat.slice(1).toLowerCase(); // headers should always be left-most column
      formatList.push({
        style: "sectionHeader",
        level: 0,
        indentColumn,
        group: [],
      });
      result.push(sectionHeader);
      const phaseCatItems = phaseCatGrouped[phaseCat] || [];
      const grouped = phaseCatItems.reduce(
        (acc: any, item: any) => {
          const row: (string | number | null)[] = Array(maxColumnIndex + 1).fill(null);
          Object.keys(columnMap).forEach((key) => {
            if (key in item) {
              if (key == "allocation") {
                row[columnMap[key]] = item.allocation / 100;
              } else {
                row[columnMap[key]] = item[key];
              }
            } else if (key in calculatedRangeNames) {
              row[columnMap[key]] = calculatedRangeNames[key](item);
            } else if (key == "uom") {
              row[columnMap[key]] = "wks";
            }
          });
          const blankString = "blank";
          const groupKey = item[groupByProp] || blankString;
          acc[groupKey] = acc[groupKey] || [];
          acc[groupKey].push(row);
          return acc;
        },
        {} as { [key: string]: Record<string, any>[] }
      );
      const priorityGroups = ["Construction", "Precon"];
      const sortedGroupKeys = Object.keys(grouped).sort((a, b) => {
        const priorityA = priorityGroups.indexOf(a);
        const priorityB = priorityGroups.indexOf(b);

        if (priorityA !== -1 || priorityB !== -1) {
          if (priorityA === -1) return 1;
          if (priorityB === -1) return -1;
          return priorityA - priorityB;
        }

        const phaseA = staffPhases?.find((phase: Record<string, any>) => phase.name === a);
        const phaseB = staffPhases?.find((phase: Record<string, any>) => phase.name === b);

        const dateA = phaseA?.start_date ? new Date(phaseA.start_date) : new Date(0);
        const dateB = phaseB?.start_date ? new Date(phaseB.start_date) : new Date(0);

        //  Sort by start_date descending
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA.getTime() - dateB.getTime();
        }

        // If start_date is the same, sort by shortest duration
        const finishA = phaseA?.finish_date ? new Date(phaseA.finish_date) : new Date(0);
        const finishB = phaseB?.finish_date ? new Date(phaseB.finish_date) : new Date(0);

        const durationA = finishA.getTime() - dateA.getTime();
        const durationB = finishB.getTime() - dateB.getTime();

        if (durationA !== durationB) {
          return durationA - durationB; // Shorter duration first
        }

        // Alphanumeric fallback
        return a.localeCompare(b);
      });
      sortedGroupKeys.forEach((groupKey, index) => {
        // Add group header
        const headerRow: (string | number | null)[] = Array(maxColumnIndex + 1).fill(null);
        headerRow[0] = groupKey; // headers should always be left-most column
        formatList.push({
          style: "header",
          level: 1,
          indentColumn,
          group: Array.from({ length: 1 }, (_, i) => i + 1),
        });

        // Add staff items to the group
        result.push(headerRow, ...grouped[groupKey]);
        for (let j = 0; j < grouped[groupKey].length; j++) {
          formatList.push({
            style: "item",
            level: 2,
            indentColumn,
            group: Array.from({ length: 2 }, (_, i) => i + 1),
          });
        }

        if (subtotalsInHeader) {
          // Add subtotal formula to the header row's total column
          const itemCount = grouped[groupKey].length;
          headerRow[updatedColumnMap.grand_total] = `=SUBTOTAL(9, R[1]C:R[${itemCount}]C)`;
        } else {
          // Add subtotal row
          const itemCount = grouped[groupKey].length;
          const subtotalRow: (string | number | null)[] = Array(maxColumnIndex + 1).fill(null);
          subtotalRow[updatedColumnMap.grand_total - 1] = `${groupKey} Subtotal`; // Subtotal should always be right of total
          subtotalRow[updatedColumnMap.grand_total] = `=SUBTOTAL(9, R[-${itemCount}]C:R[-1]C)`;

          result.push(subtotalRow);
          formatList.push({
            style: "subtotal",
            level: 0,
            indentColumn,
            group: Array.from({ length: 1 }, (_, i) => i + 1),
          });
        }

        // Add a blank row between top-level groups, if enabled
        if (addBlankRowBetweenGroups && index < sortedGroupKeys.length - 1) {
          // Fill blank/spacer row with empty strings to override formulas copied from first row in print formatted data
          const blankRow: (string | number | null)[] = Array(maxColumnIndex + 1).fill("");
          result.push(blankRow);
          formatList.push({
            style: "spacer",
            level: 0,
            indentColumn,
            group: [],
          });
        }
      });

      if (subtotalsInHeader) {
        // Add subtotal formula to section header row's total column
        const sectionItemCount = phaseCatPhases[phaseCat].size + phaseCatItems.length;
        sectionHeader[updatedColumnMap.grand_total] = `=SUBTOTAL(9, R[1]C:R[${sectionItemCount}]C)`;
      } else {
        // Add subtotal row
        const sectionItemCount = phaseCatPhases[phaseCat].size + phaseCatItems.length;
        const includeSpaceCount = phaseCatPhases[phaseCat].size - 1;
        const subtotalRow: (string | number | null)[] = Array(maxColumnIndex + 1).fill(null);
        subtotalRow[updatedColumnMap.grand_total - 1] = `${phaseCat} Subtotal`; // Subtotal should always be right of total
        subtotalRow[updatedColumnMap.grand_total] = `=SUBTOTAL(9, R[-${sectionItemCount + includeSpaceCount}]C:R[-1]C)`;

        result.push(subtotalRow);
        formatList.push({
          style: "subtotal",
          level: 0,
          indentColumn,
          group: Array.from({ length: 1 }, (_, i) => i + 1),
        });
      }

      // Add a blank row between top-level groups, if enabled
      if (index < sortedPhaseCategories.length - 1) {
        // Fill blank/spacer row with empty strings to override formulas copied from first row in print formatted data
        const blankRow: (string | number | null)[] = Array(maxColumnIndex + 1).fill("");
        result.push(blankRow);
        formatList.push({
          style: "spacer",
          level: 0,
          indentColumn,
          group: [],
        });
      }
    });

    return result;
  };

  // Start grouping from level 0
  const dataArray = groupData(staffItems || []);
  return { dataArray, formatList };
};

export const generateGCGRLineReport = async (worksheet: Excel.Worksheet) => {
  const startTime = performance.now();
  const options = (await getReportOptions(worksheet, gcgrLineItemsDefaultOptions)) as GCGRReportOptions;

  const projectId = await resolveProjectId();
  const resolved = await fetchResolvedGCGRExport(projectId);
  if (!resolved) {
    throw new ValidationError("Load GCGR data before generating report.");
  }
  const { fullGCGR } = resolved;

  const lineItemsRaw: GCGRLineItem[] = fullGCGR.gcgr_detail_line_items || [];
  const groupsRes = await fetchGCGRDetailGroups();
  if (!groupsRes.success) throw groupsRes.error;
  const detailGroups = groupsRes.data || [];
  const lineItems = transformGcgrLineItems(lineItemsRaw, detailGroups);

  const gcItems = lineItems.filter((item) => item.reporting_type === "GC");
  const grItems = lineItems.filter((item) => item.reporting_type === "GR");

  if (!gcItems.length && !grItems.length) {
    throw new ValidationError("Load GCGR data before generating report.");
  }

  const columnMap = await createColumnNameToIndexMap(worksheet);
  //let currentRow = 1;

  const { dataArray: lineItemArray, formatList: lineItemFormatList } = gcgrLineReportArray({
    lineItems: lineItems,
    detailGroups: detailGroups,
    columnMap,
    options,
  });

  const startColumnIndex = columnMap.startColumnIndex;
  await printFormattedData({
    dataArray: lineItemArray,
    formatList: lineItemFormatList,
    worksheet,
    insertAtRangeName: rowNames.gcgrLineItems,
    startColumnIndex,
    options,
    columnMap,
  });

  await updateReportDate(worksheet);
  await setPrintAreaToUsedRange(worksheet);

  track("excel.createReport.gcgrlinetems", {
    loadTime: (performance.now() - startTime) / 1000,
  });
};

const gcgrLineReportArray = ({
  lineItems,
  detailGroups,
  columnMap,
  options,
}: GCGRarrayArgs): { dataArray: (string | number | null)[][]; formatList: RowFormat[] } => {
  // Sort data by `order` and `name`
  const formatList: RowFormat[] = [];

  // Determine the maximum column index from columnMap
  let maxColumnIndex = Math.max(...Object.values(columnMap));
  const indentColumn = columnMap.name;
  let updatedColumnMap = { ...columnMap };
  const { subtotalsInHeader, addBlankRowBetweenGroups } = options;

  // Recursive function to group data
  const groupData = (lineItems: Record<string, any>[]): (string | number | null)[][] => {
    // line items section
    const sortedItems = lineItems
      // filter out items that are not reporting_type GC or GR
      .filter((item) => item.reporting_type === "GC" || item.reporting_type === "GR")
      .sort((a, b) => {
        // Sort by reporting_type, with "GC" always first
        if (a.reporting_type === "GC" && b.reporting_type !== "GC") return -1;
        if (a.reporting_type !== "GC" && b.reporting_type === "GC") return 1;

        // If same reporting_type, sort by group alphabetically
        const groupCompare = a.group.localeCompare(b.group);
        if (groupCompare !== 0) return groupCompare;

        // If same group, sort by sort_order
        return (a.order ?? 0) - (b.order ?? 0);
      });

    const reportTypeGrouped = sortedItems.reduce(
      (acc, item) => {
        acc[item.reporting_type] = acc[item.reporting_type] || [];
        acc[item.reporting_type].push(item);
        return acc;
      },
      {} as { [key: string]: Record<string, any>[] }
    );

    const reportTypeDetailGroups = sortedItems.reduce(
      (acc, item) => {
        if (!acc[item.reporting_type]) {
          acc[item.reporting_type] = new Set<string>(); // Use Set for uniqueness
        }
        acc[item.reporting_type].add(item.group); // Add to Set
        return acc;
      },
      {} as { [key: string]: Set<string> }
    );

    const sortedReportType = Object.keys(reportTypeGrouped).sort((a, b) => {
      if (a === "GC" && b !== "GC") return -1;
      if (a !== "GC" && b === "GC") return 1;
      return a.localeCompare(b);
    });

    const result: (string | number | null)[][] = [];

    sortedReportType.forEach((reportType, index) => {
      // Add section header for report type
      const sectionHeader: (string | number | null)[] = Array(maxColumnIndex + 1).fill(null);
      sectionHeader[0] = reportType; // headers should always be left-most column
      formatList.push({
        style: "sectionHeader",
        level: 0,
        indentColumn,
        group: [],
      });
      result.push(sectionHeader);
      const reportTypeItems = reportTypeGrouped[reportType] || [];
      const grouped = reportTypeItems.reduce(
        (acc: any, item: any) => {
          const blankString = "blank";
          const groupKey = item.group || blankString;
          acc[groupKey] = acc[groupKey] || [];
          // Build row dynamically using columnMap
          const row: (string | number | null)[] = Array(maxColumnIndex + 1).fill(null);
          Object.keys(columnMap).forEach((key) => {
            if (key in item) {
              row[columnMap[key]] = item[key];
            } else if (key in calculatedRangeNames) {
              row[columnMap[key]] = calculatedRangeNames[key](item);
            }
          });
          acc[groupKey].push(row);
          return acc;
        },
        {} as { [key: string]: Record<string, any>[] }
      );
      const sortedGroupKeys = Object.keys(grouped).sort((a, b) => {
        const sortOrderA = detailGroups?.find((group: Record<string, any>) => group.name === a)?.sort_order ?? 0;
        const sortOrderB = detailGroups?.find((group: Record<string, any>) => group.name === b)?.sort_order ?? 0;
        if (sortOrderA === sortOrderB) {
          return a.localeCompare(b);
        }

        return sortOrderA - sortOrderB;
      });
      sortedGroupKeys.forEach((groupKey, index) => {
        // Add group header
        const headerRow: (string | number | null)[] = Array(maxColumnIndex + 1).fill(null);
        headerRow[0] = groupKey; // headers should always be left-most column
        formatList.push({
          style: "header",
          level: 1,
          indentColumn,
          group: Array.from({ length: 1 }, (_, i) => i + 1),
        });

        // Add line items to the group
        result.push(headerRow, ...grouped[groupKey]);
        for (let j = 0; j < grouped[groupKey].length; j++) {
          formatList.push({
            style: "item",
            level: 2,
            indentColumn,
            group: Array.from({ length: 2 }, (_, i) => i + 1),
          });
        }

        if (subtotalsInHeader) {
          // Add subtotal formula to the header row's total column
          const itemCount = grouped[groupKey].length;
          headerRow[updatedColumnMap.total] = `=SUBTOTAL(9, R[1]C:R[${itemCount}]C)`;
        } else {
          // Add subtotal row
          const itemCount = grouped[groupKey].length;
          const subtotalRow: (string | number | null)[] = Array(maxColumnIndex + 1).fill(null);
          subtotalRow[updatedColumnMap.total - 1] = `${groupKey} Subtotal`; // Subtotal should always be right of total
          subtotalRow[updatedColumnMap.total] = `=SUBTOTAL(9, R[-${itemCount}]C:R[-1]C)`;

          result.push(subtotalRow);
          formatList.push({
            style: "subtotal",
            level: 0,
            indentColumn,
            group: Array.from({ length: 1 }, (_, i) => i + 1),
          });
        }

        // Add a blank row between top-level groups, if enabled
        if (addBlankRowBetweenGroups && index < sortedGroupKeys.length - 1) {
          // Fill blank/spacer row with empty strings to override formulas copied from first row in print formatted data
          const blankRow: (string | number | null)[] = Array(maxColumnIndex + 1).fill("");
          result.push(blankRow);
          formatList.push({
            style: "spacer",
            level: 0,
            indentColumn,
            group: [],
          });
        }
      });

      if (subtotalsInHeader) {
        // Add subtotal formula to section header row's total column
        const sectionItemCount = reportTypeDetailGroups[reportType].size + reportTypeItems.length;
        sectionHeader[updatedColumnMap.total] = `=SUBTOTAL(9, R[1]C:R[${sectionItemCount}]C)`;
      } else {
        // Add subtotal row
        const sectionItemCount = reportTypeDetailGroups[reportType].size + reportTypeItems.length;
        const includeSpaceCount = reportTypeDetailGroups[reportType].size - 1;
        const subtotalRow: (string | number | null)[] = Array(maxColumnIndex + 1).fill(null);
        subtotalRow[updatedColumnMap.total - 1] = `${reportType} Subtotal`; // Subtotal should always be right of total
        subtotalRow[updatedColumnMap.total] = `=SUBTOTAL(9, R[-${sectionItemCount + includeSpaceCount}]C:R[-1]C)`;

        result.push(subtotalRow);
        formatList.push({
          style: "subtotal",
          level: 0,
          indentColumn,
          group: Array.from({ length: 1 }, (_, i) => i + 1),
        });
      }

      // Add a blank row between top-level groups, if enabled
      if (index < sortedReportType.length - 1) {
        // Fill blank/spacer row with empty strings to override formulas copied from first row in print formatted data
        const blankRow: (string | number | null)[] = Array(maxColumnIndex + 1).fill("");
        result.push(blankRow);
        formatList.push({
          style: "spacer",
          level: 0,
          indentColumn,
          group: [],
        });
      }
    });

    return result;
  };

  // Start grouping from level 0
  const dataArray = groupData(lineItems || []);
  return { dataArray, formatList };
};
